import { useState, useEffect } from 'react';
import api from '../functions/api';

// Simple in-memory cache keyed by dbName ('dashboard' or '__ALL__')
const schemaCache = {};

const useFetchDashboardStructure = (enabled = true, dbName) => {
  const [dashboardStructure, setDashboardStructure] = useState(null);
  const [allDatabasesStructure, setAllDatabasesStructure] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [databaseErrors, setDatabaseErrors] = useState(null);

  useEffect(() => {
    if (!enabled) {
      // Do not fetch until enabled; ensure not showing spinner when disabled
      setLoading(false);
      return;
    }
    const cacheKey = dbName || '__ALL__';
    // If we have cached schema, serve immediately
    if (schemaCache[cacheKey]) {
      const cached = schemaCache[cacheKey];
      setDashboardStructure(cached.dashboardStructure || null);
      setAllDatabasesStructure(cached.allDatabasesStructure || null);
      setDatabaseErrors(cached.databaseErrors || null);
      setError(null);
      setLoading(false);
      return; // Skip network for speed
    }
    // Reset state when no cache
    setLoading(true);
    setError(null);
    setDatabaseErrors(null);
    setAllDatabasesStructure(null);
    setDashboardStructure(null);

    const fetchDashboardStructure = async () => {
      try {
        // DAS-1473: Request specific database if provided; otherwise all
        const payload = dbName ? { db_name: dbName } : undefined;
        const response = await api.post('/get_dashboard_table_structure', payload);
        
        // Check for API error response
        if (response.data.error) {
          const errorMsg = `API Error: ${response.data.error}`;
          console.error(errorMsg, response.data.traceback || '');
          setError(errorMsg);
          setLoading(false);
          return;
        }
        
        // Handle both legacy format (dashboard_structure) and new format (all_databases_structure)
        if (response.data.all_databases_structure) {
          setAllDatabasesStructure(response.data.all_databases_structure);
          // Capture database errors if present (non-blocking warnings)
          if (response.data.database_errors) {
            setDatabaseErrors(response.data.database_errors);
          }
          // For backward compatibility, also set dashboard_structure if it exists
          if (response.data.all_databases_structure.dashboard) {
            setDashboardStructure(response.data.all_databases_structure.dashboard);
          }
          // Cache result
          schemaCache[cacheKey] = {
            allDatabasesStructure: response.data.all_databases_structure,
            dashboardStructure: response.data.all_databases_structure.dashboard,
            databaseErrors: response.data.database_errors || null
          };
        } else if (response.data.dashboard_structure) {
          // Legacy format: single database
          setDashboardStructure(response.data.dashboard_structure);
          setAllDatabasesStructure({ dashboard: response.data.dashboard_structure });
          // Cache result
          schemaCache[cacheKey] = {
            allDatabasesStructure: { dashboard: response.data.dashboard_structure },
            dashboardStructure: response.data.dashboard_structure,
            databaseErrors: null
          };
        } else {
          setError('Unexpected response format from API');
          console.error('Unexpected API response:', response.data);
        }
      } catch (error) {
        const errorMsg = error.response?.data?.error || error.message || 'Error fetching dashboard structure';
        setError(errorMsg);
        console.error('Error fetching dashboard structure:', error);
        if (error.response?.data?.traceback) {
          console.error('Server traceback:', error.response.data.traceback);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardStructure();
  }, [enabled, dbName]);

  return { dashboardStructure, allDatabasesStructure, loading, error, databaseErrors };
};

export default useFetchDashboardStructure;
